/**
 * Page-view tracker.
 * Writes one row to page_views per route change, debounced so rapid
 * programmatic navigations coalesce. FoxChat reads the last ~100 rows per
 * user to personalise answers ("user was on Dealflow 10s ago") and to
 * suggest features they haven't opened yet.
 *
 * Keeps noise low:
 *   - ignores anchor-only changes
 *   - collapses consecutive identical paths
 *   - trims the user's tail to 200 rows on insert (best-effort cleanup)
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const TRIM_RETAIN = 200;

export function usePageViewTracker() {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const path = location.pathname;
    if (!path) return;
    if (path === lastPath.current) return;
    lastPath.current = path;

    // Let walkthroughs listen for React-Router nav (popstate doesn't fire on pushState).
    try { window.dispatchEvent(new CustomEvent("foxchat:nav", { detail: { pathname: path } })); } catch { /* ignore */ }

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data: u } = await supabase.auth.getUser();
        const userId = u?.user?.id || null;
        if (!userId) return; // don't log anon traffic — nothing to personalise against
        await supabase.from("page_views" as any).insert({ user_id: userId, path });
        // Best-effort tail trim. Runs rarely (only when a new view is logged)
        // and is bounded by user_id index, so it's cheap.
        try {
          const { data: rows } = await supabase
            .from("page_views" as any)
            .select("id, viewed_at")
            .eq("user_id", userId)
            .order("viewed_at", { ascending: false })
            .range(TRIM_RETAIN, TRIM_RETAIN + 200);
          const toDelete = ((rows as any[]) || []).map((r) => r.id);
          if (toDelete.length > 0) {
            await supabase.from("page_views" as any).delete().in("id", toDelete);
          }
        } catch { /* trim is optional */ }
      } catch { /* tracking must never break UX */ }
    }, 600);

    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [location.pathname]);
}
